import React, { useMemo } from 'react';
import PitchCanvas from '../PitchCanvas';
import { TeamShapePlot, NetworkLines } from '../visualizations';
import AttackingDirectionControls from './AttackingDirectionControls';
import ColorStyleControls from '../controls/ColorStyleControls';
import { UseMapVisualizationReturn } from '../../../hooks/useMapVisualization';
import { StatType } from '../../../types/database';
import { Cluster } from '../../../utils/clustering';
import { VisualizationEvent } from '../types';
import { calculateNetworkConnections } from '../../../utils/teamShapeNetwork';

interface TeamShapeRendererProps {
  // Compare mode props
  compareMode: boolean;
  homeClusters: Cluster[];
  awayClusters: Cluster[];
  // Single mode props
  showBothTeams: boolean;
  homeClustersSingle: Cluster[];
  awayClustersSingle: Cluster[];
  clusters: Cluster[];
  selectedTeamForShape: string;
  // Common props
  mapVisualization: UseMapVisualizationReturn;
  statTypes: StatType[];
  events: VisualizationEvent[];
  homeTeamId: string;
  awayTeamId: string;
  homeTeamName: string;
  awayTeamName: string;
  handleHomeTeamDirectionChange: (direction: 'left' | 'right') => void;
  handleAwayTeamDirectionChange: (direction: 'left' | 'right') => void;
  // Network props
  showNetworks?: boolean;
  // Loading/Error states
  combinedShapeLoading: boolean;
  combinedShapeError: string | null;
  homePointCount: number;
  awayPointCount: number;
  pointCount: number;
}

/**
 * TeamShapeRenderer - Renders team shape visualizations
 * Handles both compare mode and single view mode
 * Extracted from MapsTab to reduce component size
 */
const TeamShapeRenderer: React.FC<TeamShapeRendererProps> = ({
  compareMode,
  homeClusters,
  awayClusters,
  showBothTeams,
  homeClustersSingle,
  awayClustersSingle,
  clusters,
  selectedTeamForShape,
  mapVisualization,
  statTypes,
  events,
  homeTeamId,
  awayTeamId,
  homeTeamName,
  awayTeamName,
  handleHomeTeamDirectionChange,
  handleAwayTeamDirectionChange,
  showNetworks = false,
  combinedShapeLoading,
  combinedShapeError,
  homePointCount,
  awayPointCount,
  pointCount,
}) => {
  // Build stat_key map for network calculation
  const statKeyMap = useMemo(() => {
    const map = new Map<string, string>();
    statTypes.forEach(statType => {
      map.set(statType.id, statType.stat_key);
    });
    return map;
  }, [statTypes]);

  // Calculate network connections for home team
  const homeConnections = useMemo(() => {
    if (!showNetworks || homeClusters.length === 0) return [];
    const homeEvents = events.filter(e => e.team_id === homeTeamId);
    return calculateNetworkConnections(homeEvents, homeClusters, statKeyMap);
  }, [showNetworks, homeClusters, events, homeTeamId, statKeyMap]);

  // Calculate network connections for away team
  const awayConnections = useMemo(() => {
    if (!showNetworks || awayClusters.length === 0) return [];
    const awayEvents = events.filter(e => e.team_id === awayTeamId);
    return calculateNetworkConnections(awayEvents, awayClusters, statKeyMap);
  }, [showNetworks, awayClusters, events, awayTeamId, statKeyMap]);

  // Calculate network connections for single view (home)
  const homeConnectionsSingle = useMemo(() => {
    if (!showNetworks || homeClustersSingle.length === 0) return [];
    const homeEvents = events.filter(e => e.team_id === homeTeamId);
    return calculateNetworkConnections(homeEvents, homeClustersSingle, statKeyMap);
  }, [showNetworks, homeClustersSingle, events, homeTeamId, statKeyMap]);

  // Calculate network connections for single view (away)
  const awayConnectionsSingle = useMemo(() => {
    if (!showNetworks || awayClustersSingle.length === 0) return [];
    const awayEvents = events.filter(e => e.team_id === awayTeamId);
    return calculateNetworkConnections(awayEvents, awayClustersSingle, statKeyMap);
  }, [showNetworks, awayClustersSingle, events, awayTeamId, statKeyMap]);

  // Calculate network connections for single team view
  const singleConnections = useMemo(() => {
    if (!showNetworks || clusters.length === 0) return [];
    const teamEvents = events.filter(e => e.team_id === selectedTeamForShape);
    return calculateNetworkConnections(teamEvents, clusters, statKeyMap);
  }, [showNetworks, clusters, events, selectedTeamForShape, statKeyMap]);
  if (compareMode) {
    // Compare Mode: Two pitches side by side
    const allClustersCompare = [...homeClusters, ...awayClusters];
    const allWeightsCompare = allClustersCompare.map(c => c.totalWeight);
    const sharedSizeRangeCompare = allWeightsCompare.length > 0
      ? {
          min: Math.min(...allWeightsCompare),
          max: Math.max(...allWeightsCompare)
        }
      : { min: 0, max: 0 };
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 pt-2 pb-3">
        <div className="flex flex-col">
          <div className="grid grid-cols-2 gap-4">
            {/* Home Team Pitch */}
            <div className="flex flex-col">
              <AttackingDirectionControls
                teamName={homeTeamName}
                attackingDirection={mapVisualization.homeTeamAttackingDirection}
                normalizeToFirstHalf={mapVisualization.normalizeToFirstHalf}
                onDirectionChange={handleHomeTeamDirectionChange}
              />
              <div className="relative">
                <PitchCanvas className="w-full" whiteBackground={mapVisualization.whiteBackground} rotation={0}>
                  <TeamShapePlot
                    clusters={homeClusters}
                    rotation={0}
                    scale={1.0}
                    teamColor={mapVisualization.teamColors[homeTeamId] || '#3B82F6'}
                    minSize={12}
                    maxSize={40}
                    opacity={0.7}
                    sizeRange={sharedSizeRangeCompare}
                  />
                  {showNetworks && (
                    <NetworkLines
                      clusters={homeClusters}
                      connections={homeConnections}
                      rotation={0}
                      scale={1.0}
                      lineColor={mapVisualization.teamColors[homeTeamId] || '#3B82F6'}
                      minThickness={1}
                      maxThickness={6}
                      opacity={0.6}
                    />
                  )}
                </PitchCanvas>
              </div>
            </div>

            {/* Away Team Pitch */}
            <div className="flex flex-col">
              <AttackingDirectionControls
                teamName={awayTeamName}
                attackingDirection={mapVisualization.awayTeamAttackingDirection}
                normalizeToFirstHalf={mapVisualization.normalizeToFirstHalf}
                onDirectionChange={handleAwayTeamDirectionChange}
              />
              <div className="relative">
                <PitchCanvas className="w-full" whiteBackground={mapVisualization.whiteBackground} rotation={0}>
                  <TeamShapePlot
                    clusters={awayClusters}
                    rotation={0}
                    scale={1.0}
                    teamColor={mapVisualization.teamColors[awayTeamId] || '#EF4444'}
                    minSize={12}
                    maxSize={40}
                    opacity={0.7}
                    sizeRange={sharedSizeRangeCompare}
                  />
                  {showNetworks && (
                    <NetworkLines
                      clusters={awayClusters}
                      connections={awayConnections}
                      rotation={0}
                      scale={1.0}
                      lineColor={mapVisualization.teamColors[awayTeamId] || '#EF4444'}
                      minThickness={1}
                      maxThickness={6}
                      opacity={0.6}
                    />
                  )}
                </PitchCanvas>
              </div>
            </div>
          </div>

          {/* Legend with Color Swatches - Show when networks are enabled in compare mode */}
          {showNetworks && (
            <ColorStyleControls
              mapVisualization={mapVisualization}
              statTypes={statTypes}
              events={events}
              homeTeamId={homeTeamId}
              awayTeamId={awayTeamId}
              homeTeamName={homeTeamName}
              awayTeamName={awayTeamName}
              compareMode={true}
            />
          )}
        </div>
      </div>
    );
  }

  // Single Pitch Mode
  const allClusters = [...homeClustersSingle, ...awayClustersSingle];
  const allWeights = allClusters.map(c => c.totalWeight);
  const sharedSizeRange = allWeights.length > 0
    ? {
        min: Math.min(...allWeights),
        max: Math.max(...allWeights)
      }
    : { min: 0, max: 0 };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-3 pt-2 pb-3">
      <div className="flex flex-col">
        {/* Attacking Direction Controls */}
        {showBothTeams ? (
          // Both teams - show both labels side by side
          <div className="flex items-center justify-between mb-1">
            {/* Home Team */}
            <AttackingDirectionControls
              teamName={homeTeamName}
              attackingDirection={mapVisualization.homeTeamAttackingDirection}
              normalizeToFirstHalf={mapVisualization.normalizeToFirstHalf}
              onDirectionChange={handleHomeTeamDirectionChange}
            />
            
            {/* Away Team */}
            <AttackingDirectionControls
              teamName={awayTeamName}
              attackingDirection={mapVisualization.awayTeamAttackingDirection}
              normalizeToFirstHalf={mapVisualization.normalizeToFirstHalf}
              onDirectionChange={handleAwayTeamDirectionChange}
            />
          </div>
        ) : selectedTeamForShape === homeTeamId ? (
          <AttackingDirectionControls
            teamName={homeTeamName}
            attackingDirection={mapVisualization.homeTeamAttackingDirection}
            normalizeToFirstHalf={mapVisualization.normalizeToFirstHalf}
            onDirectionChange={handleHomeTeamDirectionChange}
          />
        ) : selectedTeamForShape === awayTeamId ? (
          <AttackingDirectionControls
            teamName={awayTeamName}
            attackingDirection={mapVisualization.awayTeamAttackingDirection}
            normalizeToFirstHalf={mapVisualization.normalizeToFirstHalf}
            onDirectionChange={handleAwayTeamDirectionChange}
          />
        ) : null}

        <div className="relative">
          <PitchCanvas className="w-full" whiteBackground={mapVisualization.whiteBackground} rotation={0}>
            {/* Render both teams' shapes when "Both Teams" is selected */}
            {showBothTeams ? (
              <>
                <TeamShapePlot
                  clusters={homeClustersSingle}
                  rotation={0}
                  scale={1.0}
                  teamColor={mapVisualization.teamColors[homeTeamId] || '#3B82F6'}
                  minSize={12}
                  maxSize={40}
                  opacity={0.7}
                  sizeRange={sharedSizeRange}
                />
                {showNetworks && (
                  <NetworkLines
                    clusters={homeClustersSingle}
                    connections={homeConnectionsSingle}
                    rotation={0}
                    scale={1.0}
                    lineColor={mapVisualization.teamColors[homeTeamId] || '#3B82F6'}
                    minThickness={1}
                    maxThickness={6}
                    opacity={0.6}
                  />
                )}
                <TeamShapePlot
                  clusters={awayClustersSingle}
                  rotation={0}
                  scale={1.0}
                  teamColor={mapVisualization.teamColors[awayTeamId] || '#EF4444'}
                  minSize={12}
                  maxSize={40}
                  opacity={0.7}
                  sizeRange={sharedSizeRange}
                />
                {showNetworks && (
                  <NetworkLines
                    clusters={awayClustersSingle}
                    connections={awayConnectionsSingle}
                    rotation={0}
                    scale={1.0}
                    lineColor={mapVisualization.teamColors[awayTeamId] || '#EF4444'}
                    minThickness={1}
                    maxThickness={6}
                    opacity={0.6}
                  />
                )}
              </>
            ) : (
              <>
                <TeamShapePlot
                  clusters={clusters}
                  rotation={0}
                  scale={1.0}
                  teamColor={mapVisualization.teamColors[selectedTeamForShape] || '#3B82F6'}
                  minSize={12}
                  maxSize={40}
                  opacity={0.7}
                />
                {showNetworks && (
                  <NetworkLines
                    clusters={clusters}
                    connections={singleConnections}
                    rotation={0}
                    scale={1.0}
                    lineColor={mapVisualization.teamColors[selectedTeamForShape] || '#3B82F6'}
                    minThickness={1}
                    maxThickness={6}
                    opacity={0.6}
                  />
                )}
              </>
            )}
          </PitchCanvas>
        </div>

        {/* Legend with Color Swatches - Show when both teams are selected or when networks are enabled */}
        {(showBothTeams || showNetworks) && (
          <ColorStyleControls
            mapVisualization={mapVisualization}
            statTypes={statTypes}
            events={events}
            homeTeamId={homeTeamId}
            awayTeamId={awayTeamId}
            homeTeamName={homeTeamName}
            awayTeamName={awayTeamName}
            compareMode={false}
          />
        )}

        {/* Error/Empty States */}
        {combinedShapeError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
            <p className="text-red-800 text-sm">{combinedShapeError}</p>
          </div>
        )}

        {!combinedShapeLoading && showBothTeams && homeClustersSingle.length === 0 && awayClustersSingle.length === 0 && (
          <div className="text-center py-12 mt-4">
            <p className="text-gray-500">
              {homePointCount === 0 && awayPointCount === 0
                ? 'No position data available for either team. Position data will appear here once events are recorded.'
                : 'Unable to calculate team shapes. Try adjusting the configuration.'}
            </p>
          </div>
        )}

        {!combinedShapeLoading && !showBothTeams && clusters.length === 0 && (
          <div className="text-center py-12 mt-4">
            <p className="text-gray-500">
              {pointCount === 0 
                ? 'No position data available for this team. Position data will appear here once events are recorded.'
                : 'Unable to calculate team shape. Try adjusting the configuration.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamShapeRenderer;

